/**
 * Attachment processing utilities for Playwright reports
 */

import { relative, resolve, isAbsolute } from 'path';
import { exists } from '@/utils/fs';
import { PlaywrightReport } from './parser';
import { logger } from '@/utils/logger';

/**
 * Attachment information extracted from Playwright report
 */
export interface AttachmentInfo {
  name: string;
  contentType: string;
  originalPath: string;
  relativePath: string;
  absolutePath: string;
  type: 'image' | 'video' | 'trace' | 'file' | 'other';
}

/**
 * Result of attachment scanning process
 */
export interface AttachmentScanResult {
  images: AttachmentInfo[];
  videos: AttachmentInfo[];
  traces: AttachmentInfo[];
  files: AttachmentInfo[];
  other: AttachmentInfo[];
  total: number;
}

/**
 * Content type mappings for attachment classification
 */
const CONTENT_TYPE_MAPPING = {
  image: [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    'image/bmp',
    'image/tiff',
  ],
  video: ['video/webm', 'video/mp4', 'video/avi', 'video/mov', 'video/mkv'],
  trace: [
    'application/zip',
    'application/x-zip-compressed',
    'application/octet-stream', // Common for .trace files
  ],
  file: [
    'text/markdown',
    'text/plain',
    'application/pdf',
    'text/x-log',
    'application/octet-stream', // Sometimes used for .log files
  ],
} as const;

/**
 * Service for scanning and processing attachments from Playwright reports
 */
export class AttachmentScanner {
  constructor(private readonly baseDirectory: string) {}

  /**
   * Scan Playwright report for all attachments
   */
  async scanAttachments(
    report: PlaywrightReport
  ): Promise<AttachmentScanResult> {
    const attachments: AttachmentInfo[] = [];

    // Recursively scan all test suites and specs
    for (const suite of report.suites || []) {
      await this.scanSuite(suite, attachments);
    }

    // Classify attachments by type
    const result: AttachmentScanResult = {
      images: [],
      videos: [],
      traces: [],
      files: [],
      other: [],
      total: attachments.length,
    };

    for (const attachment of attachments) {
      switch (attachment.type) {
        case 'image':
          result.images.push(attachment);
          break;
        case 'video':
          result.videos.push(attachment);
          break;
        case 'trace':
          result.traces.push(attachment);
          break;
        case 'file':
          result.files.push(attachment);
          break;
        default:
          result.other.push(attachment);
          break;
      }
    }

    return result;
  }

  /**
   * Recursively scan a test suite for attachments
   */
  private async scanSuite(
    suite: unknown,
    attachments: AttachmentInfo[]
  ): Promise<void> {
    const suiteRecord = suite as Record<string, unknown>;

    // Scan specs in this suite
    if (Array.isArray(suiteRecord.specs)) {
      for (const spec of suiteRecord.specs as unknown[]) {
        await this.scanSpec(spec, attachments);
      }
    }

    // Scan nested suites
    if (Array.isArray(suiteRecord.suites)) {
      for (const nestedSuite of suiteRecord.suites as unknown[]) {
        await this.scanSuite(nestedSuite, attachments);
      }
    }
  }

  /**
   * Scan a spec for attachments
   */
  private async scanSpec(
    spec: unknown,
    attachments: AttachmentInfo[]
  ): Promise<void> {
    const specRecord = spec as Record<string, unknown>;
    if (!Array.isArray(specRecord.tests)) return;

    for (const test of specRecord.tests as unknown[]) {
      const testRecord = test as Record<string, unknown>;
      if (!Array.isArray(testRecord.results)) continue;

      for (const result of testRecord.results as unknown[]) {
        const resultRecord = result as Record<string, unknown>;
        if (Array.isArray(resultRecord.attachments)) {
          for (const attachment of resultRecord.attachments as unknown[]) {
            const attachmentInfo = await this.processAttachment(attachment);
            if (attachmentInfo) {
              attachments.push(attachmentInfo);
            }
          }
        }
      }
    }
  }

  /**
   * Process a single attachment and resolve its paths
   */
  private async processAttachment(
    attachment: unknown
  ): Promise<AttachmentInfo | null> {
    if (!attachment || typeof attachment !== 'object') {
      return null;
    }

    const attachmentRecord = attachment as Record<string, unknown>;
    const { name, contentType, path } = attachmentRecord;

    if (
      !name ||
      !contentType ||
      !path ||
      typeof name !== 'string' ||
      typeof contentType !== 'string' ||
      typeof path !== 'string'
    ) {
      return null;
    }

    // Resolve paths
    const absolutePath = isAbsolute(path)
      ? path
      : resolve(this.baseDirectory, path);

    // Check if file exists
    if (!(await exists(absolutePath))) {
      logger.warn(`Attachment file not found: ${absolutePath}`);
      return null;
    }

    // Calculate relative path from base directory
    const relativePath = relative(this.baseDirectory, absolutePath);

    // Determine attachment type
    const type = this.classifyAttachment(contentType, name);

    return {
      name,
      contentType,
      originalPath: path,
      relativePath,
      absolutePath,
      type,
    };
  }

  /**
   * Classify attachment by content type and name
   */
  private classifyAttachment(
    contentType: string,
    name: string
  ): AttachmentInfo['type'] {
    const lowerContentType = contentType.toLowerCase();
    const lowerName = name.toLowerCase();

    // Check content type mappings
    if (CONTENT_TYPE_MAPPING.image.includes(lowerContentType as 'image/png')) {
      return 'image';
    }

    if (CONTENT_TYPE_MAPPING.video.includes(lowerContentType as 'video/webm')) {
      return 'video';
    }

    if (
      CONTENT_TYPE_MAPPING.trace.includes(lowerContentType as 'application/zip')
    ) {
      // Additional check for trace files by name
      if (
        lowerName.includes('trace') ||
        lowerName.endsWith('.trace') ||
        lowerName.endsWith('.zip')
      ) {
        return 'trace';
      }
    }

    if (
      CONTENT_TYPE_MAPPING.file.includes(lowerContentType as 'text/markdown')
    ) {
      return 'file';
    }

    // Fallback classification by file extension
    if (
      lowerName.endsWith('.png') ||
      lowerName.endsWith('.jpg') ||
      lowerName.endsWith('.jpeg') ||
      lowerName.endsWith('.gif') ||
      lowerName.endsWith('.webp') ||
      lowerName.endsWith('.svg')
    ) {
      return 'image';
    }

    if (
      lowerName.endsWith('.webm') ||
      lowerName.endsWith('.mp4') ||
      lowerName.endsWith('.avi') ||
      lowerName.endsWith('.mov')
    ) {
      return 'video';
    }

    if (
      lowerName.includes('trace') ||
      lowerName.endsWith('.trace') ||
      lowerName.endsWith('.zip')
    ) {
      return 'trace';
    }

    if (
      lowerName.endsWith('.md') ||
      lowerName.endsWith('.pdf') ||
      lowerName.endsWith('.txt') ||
      lowerName.endsWith('.log')
    ) {
      return 'file';
    }

    return 'other';
  }

  /**
   * Filter attachments based on configuration flags
   */
  static filterAttachments(
    scanResult: AttachmentScanResult,
    config: {
      uploadImages: boolean;
      uploadVideos: boolean;
      uploadHtml: boolean;
      uploadTraces: boolean;
      uploadFiles: boolean;
      uploadFullJson: boolean;
    }
  ): AttachmentInfo[] {
    const filtered: AttachmentInfo[] = [];

    // uploadFullJson overrides all other flags
    if (config.uploadFullJson) {
      filtered.push(...scanResult.images);
      filtered.push(...scanResult.videos);
      filtered.push(...scanResult.files);
      return filtered;
    }

    // HTML flag includes images and videos
    if (config.uploadHtml) {
      filtered.push(...scanResult.images);
      filtered.push(...scanResult.videos);
    }

    // Individual flags (can be combined with HTML flag)
    if (config.uploadImages && !config.uploadHtml) {
      filtered.push(...scanResult.images);
    }

    if (config.uploadVideos && !config.uploadHtml) {
      filtered.push(...scanResult.videos);
    }

    if (config.uploadFiles) {
      filtered.push(...scanResult.files);
    }

    if (config.uploadTraces) {
      filtered.push(...scanResult.traces);
    }

    return filtered;
  }

  /**
   * Static version of classifyAttachment for use in static methods
   */
  private static classifyAttachmentType(
    contentType: string,
    name: string
  ): AttachmentInfo['type'] {
    const lowerContentType = contentType.toLowerCase();
    const lowerName = name.toLowerCase();

    // Check content type mappings
    if (CONTENT_TYPE_MAPPING.image.includes(lowerContentType as 'image/png')) {
      return 'image';
    }

    if (CONTENT_TYPE_MAPPING.video.includes(lowerContentType as 'video/webm')) {
      return 'video';
    }

    if (
      CONTENT_TYPE_MAPPING.trace.includes(lowerContentType as 'application/zip')
    ) {
      // Additional check for trace files by name
      if (
        lowerName.includes('trace') ||
        lowerName.endsWith('.trace') ||
        lowerName.endsWith('.zip')
      ) {
        return 'trace';
      }
    }

    if (
      CONTENT_TYPE_MAPPING.file.includes(lowerContentType as 'text/markdown')
    ) {
      return 'file';
    }

    // Fallback classification by file extension
    if (
      lowerName.endsWith('.png') ||
      lowerName.endsWith('.jpg') ||
      lowerName.endsWith('.jpeg') ||
      lowerName.endsWith('.gif') ||
      lowerName.endsWith('.webp') ||
      lowerName.endsWith('.svg')
    ) {
      return 'image';
    }

    if (
      lowerName.endsWith('.webm') ||
      lowerName.endsWith('.mp4') ||
      lowerName.endsWith('.avi') ||
      lowerName.endsWith('.mov')
    ) {
      return 'video';
    }

    if (
      lowerName.includes('trace') ||
      lowerName.endsWith('.trace') ||
      lowerName.endsWith('.zip')
    ) {
      return 'trace';
    }

    if (
      lowerName.endsWith('.md') ||
      lowerName.endsWith('.pdf') ||
      lowerName.endsWith('.txt') ||
      lowerName.endsWith('.log')
    ) {
      return 'file';
    }

    return 'other';
  }

  /**
   * Determine what should happen to an attachment path based on config
   * Returns: 'upload' | 'not-enabled' | 'not-supported' | 'unchanged'
   */
  private static getAttachmentPathAction(
    attachmentRecord: Record<string, unknown>,
    config: {
      uploadImages: boolean;
      uploadVideos: boolean;
      uploadHtml: boolean;
      uploadTraces: boolean;
      uploadFiles: boolean;
      uploadFullJson: boolean;
    }
  ): 'upload' | 'not-enabled' | 'not-supported' | 'unchanged' {
    const contentType = (attachmentRecord.contentType as string) || '';
    const name = (attachmentRecord.name as string) || '';

    // Classify the attachment type using the same logic as classifyAttachment
    const type = this.classifyAttachmentType(contentType, name);

    // uploadFullJson overrides everything
    if (config.uploadFullJson) {
      switch (type) {
        case 'image':
        case 'video':
        case 'file':
          return 'upload';
        case 'trace':
          return 'not-supported';
        default:
          return 'unchanged';
      }
    }

    // Determine action based on type and config
    switch (type) {
      case 'image':
        return config.uploadImages || config.uploadHtml
          ? 'upload'
          : 'not-enabled';
      case 'video':
        return config.uploadVideos || config.uploadHtml
          ? 'upload'
          : 'not-enabled';
      case 'file':
        return config.uploadFiles ? 'upload' : 'not-enabled';
      case 'trace':
        return config.uploadTraces ? 'upload' : 'not-enabled';
      default:
        // Other attachment types are not processed
        return 'unchanged';
    }
  }

  /**
   * Update attachment paths in the JSON report to use Azure URLs or mark as 'Not Enabled'
   */
  static updateAttachmentPaths(
    report: PlaywrightReport,
    urlMapping: Map<string, string>,
    config?: {
      uploadImages: boolean;
      uploadVideos: boolean;
      uploadHtml: boolean;
      uploadTraces: boolean;
      uploadFiles: boolean;
      uploadFullJson: boolean;
    }
  ): PlaywrightReport {
    // Deep clone the report to avoid mutations
    const updatedReport = JSON.parse(JSON.stringify(report));

    // Keep track of statistics for verbose logging
    let _uploadedCount = 0;
    let _notEnabledCount = 0;
    let _unchangedCount = 0;

    // Keep track of statistics for verbose logging
    let _notSupportedCount = 0;

    // Counter function
    const countUpdate = (newPath: string, _originalPath: string) => {
      if (newPath.startsWith('http')) {
        _uploadedCount++;
      } else if (newPath === 'Not Enabled') {
        _notEnabledCount++;
      } else if (newPath === 'Not Supported') {
        _notSupportedCount++;
      } else {
        _unchangedCount++;
      }
    };

    // Recursively update all attachment paths
    const updateSuite = (suite: unknown) => {
      if (Array.isArray((suite as Record<string, unknown>).specs)) {
        for (const spec of (suite as Record<string, unknown>)
          .specs as unknown[]) {
          updateSpec(spec);
        }
      }

      if (Array.isArray((suite as Record<string, unknown>).suites)) {
        for (const nestedSuite of (suite as Record<string, unknown>)
          .suites as unknown[]) {
          updateSuite(nestedSuite);
        }
      }
    };

    const updateSpec = (spec: unknown) => {
      const specRecord = spec as Record<string, unknown>;
      if (!Array.isArray(specRecord.tests)) return;

      for (const test of specRecord.tests as unknown[]) {
        const testRecord = test as Record<string, unknown>;
        if (!Array.isArray(testRecord.results)) continue;

        for (const result of testRecord.results as unknown[]) {
          const resultRecord = result as Record<string, unknown>;
          if (Array.isArray(resultRecord.attachments)) {
            for (const attachment of resultRecord.attachments as unknown[]) {
              const attachmentRecord = attachment as Record<string, unknown>;

              if (
                attachmentRecord.path &&
                typeof attachmentRecord.path === 'string'
              ) {
                const originalPath = attachmentRecord.path;
                let newPath = originalPath;

                // If we have the uploaded URL, use it
                if (urlMapping.has(originalPath)) {
                  newPath = urlMapping.get(originalPath) || originalPath;
                  attachmentRecord.path = newPath;
                }
                // If config is provided, determine what action to take
                else if (config) {
                  const action = this.getAttachmentPathAction(
                    attachmentRecord,
                    config
                  );

                  switch (action) {
                    case 'not-enabled':
                      newPath = 'Not Enabled';
                      attachmentRecord.path = newPath;
                      break;

                    case 'not-supported':
                      newPath = 'Not Supported';
                      attachmentRecord.path = newPath;
                      break;

                    case 'upload':
                    case 'unchanged':
                    default:
                      // Leave the original path unchanged
                      break;
                  }
                }

                countUpdate(newPath, originalPath);
              }
            }
          }
        }
      }
    };

    // Update all suites
    for (const suite of updatedReport.suites || []) {
      updateSuite(suite);
    }

    return updatedReport;
  }
}
